import { createContext, useContext } from 'react';
import type {
  CustomPageActionSubject,
  CustomPageChatContextIdentifier,
} from '@trinity/custom-pages-registry-types';
import type { ChatAPIProxyClient } from '../clients/index.js';

export type UserTerritory = {
  id: string;
  name: string;
};

type NoopCustomPageAction = {
  type: 'noop';
  payload?: object;
};

type UpdateSubjectCustomPageAction = {
  type: 'update-subject';
  payload: {
    subject: CustomPageActionSubject;
  };
};

type OpenNewChatForSubjectCustomPageAction = {
  type: 'open-new-chat-for-subject';
  payload: {
    subject: CustomPageActionSubject;
    chatContextIdentifier: CustomPageChatContextIdentifier;
  };
};

type UpdateInputCustomPageAction = {
  type: 'update-input';
  payload: {
    value: string;
  };
};

type SubmitQueryCustomPageAction = {
  type: 'submit-query';
  payload: {
    query: string;
  };
};

export type CustomPageAction =
  | NoopCustomPageAction
  | UpdateSubjectCustomPageAction
  | OpenNewChatForSubjectCustomPageAction
  | UpdateInputCustomPageAction
  | SubmitQueryCustomPageAction;
export type CustomPageDispatchAction = (action: CustomPageAction) => void;

export type CustomPageSubject = {
  id: number | string;
  name: string;
  kind?: string;
} | null;

export type CustomPageUserBrandIndication = {
  productBrandName: string;
  indicationName: string;
  isDefault: boolean;
};

export type CustomPageUser = {
  identifier: string;
  name: string;
  email: string;
  metadata: Record<string, unknown>;
  brandIndications: CustomPageUserBrandIndication[];
};

export type CustomPageBrandLookup = {
  id: string;
  name: string;
  displayName: string | null;
};

export type CustomPageBrandDrugClassProductBrandMapping = {
  drugClass: CustomPageBrandLookup;
  productBrands: CustomPageBrandLookup[];
};

export type CustomPageBrand = {
  identifier: string;
  name: string;
  productBrand?: CustomPageBrandLookup | null;
  indication?: CustomPageBrandLookup | null;
  therapeuticArea?: CustomPageBrandLookup | null;
  productBrands?: CustomPageBrandLookup[];
  drugClasses?: CustomPageBrandLookup[];
  genericDrugs?: CustomPageBrandLookup[];
  drugClassProductBrandMappings?: CustomPageBrandDrugClassProductBrandMapping[];
};

export type CustomPageArbitraryFilterFields = Record<string, unknown>;

export type CustomPageDataReadyState = {
  isDataReady: boolean;
  setDataReady: (isReady: boolean) => void;
  markDataReady: () => void;
  markDataLoading: () => void;
};

const noop = (): void => {};

export type CustomPageRendererContextValue = {
  proxiesClient: ChatAPIProxyClient | null;
  subject: CustomPageSubject;
  user: CustomPageUser;
  brand: CustomPageBrand;
  territoryId?: string;
  arbitraryFilterFields: CustomPageArbitraryFilterFields;
  setArbitraryFilterFields: (fields: CustomPageArbitraryFilterFields) => void;
  dispatchAction: CustomPageDispatchAction;
  dataReady: CustomPageDataReadyState;
};

export const CustomPageProxiesClientContext = createContext<ChatAPIProxyClient | null>(null);
export const CustomPageSubjectContext = createContext<CustomPageSubject>(null);
export const CustomPageUserContext = createContext<CustomPageUser | undefined>(undefined);
export const CustomPageBrandContext = createContext<CustomPageBrand | undefined>(undefined);
export const CustomPageTerritoryIdContext = createContext<string | undefined>(undefined);
export const CustomPageArbitraryFilterFieldsContext = createContext<CustomPageArbitraryFilterFields>({});
export const CustomPageSetArbitraryFilterFieldsContext = createContext<
  (fields: CustomPageArbitraryFilterFields) => void
>(() => {});
export const CustomPageDataReadyStateContext = createContext<CustomPageDataReadyState>({
  isDataReady: false,
  setDataReady: noop,
  markDataReady: noop,
  markDataLoading: noop,
});
const noopCustomPageDispatchAction: CustomPageDispatchAction = () => {};
export const CustomPageDispatchActionContext = createContext<CustomPageDispatchAction>(
  noopCustomPageDispatchAction
);

export const FetchUserTerritoriesContext = createContext<() => Promise<UserTerritory[]>>(() =>
  Promise.resolve([])
);

export function useCustomPageProxiesClient(): ChatAPIProxyClient {
  const ctx = useContext(CustomPageProxiesClientContext);
  if (ctx === null) {
    throw new Error('useCustomPageProxiesClient must be used within a CustomPageRendererContextProvider');
  }
  return ctx;
}

export function useCustomPageSubject(): CustomPageSubject {
  return useContext(CustomPageSubjectContext);
}

export function useCustomPageUser(): CustomPageUser {
  const ctx = useContext(CustomPageUserContext);
  if (ctx === undefined) {
    throw new Error('useCustomPageUser must be used within a CustomPageRendererContextProvider');
  }
  return ctx;
}

export function useCustomPageBrand(): CustomPageBrand {
  const ctx = useContext(CustomPageBrandContext);
  if (ctx === undefined) {
    throw new Error('useCustomPageBrand must be used within a CustomPageRendererContextProvider');
  }
  return ctx;
}

export function useCustomPageTerritoryId(): string | undefined {
  return useContext(CustomPageTerritoryIdContext);
}

export function useCustomPageArbitraryFilterFields(): CustomPageArbitraryFilterFields {
  return useContext(CustomPageArbitraryFilterFieldsContext);
}

export function useSetCustomPageArbitraryFilterFields(): (fields: CustomPageArbitraryFilterFields) => void {
  return useContext(CustomPageSetArbitraryFilterFieldsContext);
}

export function useCustomPageDispatchAction(): CustomPageDispatchAction {
  return useContext(CustomPageDispatchActionContext);
}

export function useCustomPageDataReady(): CustomPageDataReadyState {
  return useContext(CustomPageDataReadyStateContext);
}

export function useFetchUserTerritories(): () => Promise<UserTerritory[]> {
  return useContext(FetchUserTerritoriesContext);
}
